import React, { useEffect, useRef, useState } from 'react';
import {
  ACCEPT_ARCHIVO, LIMITE_ARCHIVO_BYTES, formatearTamano, validarArchivoManual,
  tipoLegible, procesarFoto, extension
} from './procesarDocumento';

// Infraestructura compartida de evidencias (M.2B.3) para Documentos y Registro
// de capacitación: cámara / selector, procesamiento de foto, validación,
// vista previa (object URLs) y guardia de salida. Cada flujo conserva su
// propio endpoint y reglas de negocio; aquí no hay ninguna llamada de red.

// Selección de UNA evidencia. `nombreFoto()` da el nombre de la foto
// procesada; `alSeleccionar(evidencia)` y `alError(texto)` informan al flujo.
export function useEvidencia({ nombreFoto, alSeleccionar, alError }) {
  const [evidencia, setEvidencia] = useState(null);
  const [procesando, setProcesando] = useState(false);
  const inputFoto = useRef(null);
  const inputArchivo = useRef(null);
  const montado = useRef(true);
  const callbacks = useRef({ alSeleccionar, alError, nombreFoto });
  callbacks.current = { alSeleccionar, alError, nombreFoto };

  // (StrictMode monta/desmonta/monta en desarrollo: el efecto restaura la marca.)
  useEffect(() => {
    montado.current = true;
    return () => { montado.current = false; };
  }, []);

  // Liberar la vista previa cuando cambia o se descarta la evidencia.
  useEffect(() => () => { if (evidencia && evidencia.preview) URL.revokeObjectURL(evidencia.preview); }, [evidencia]);

  const fijar = (ev) => {
    setEvidencia(ev);
    if (callbacks.current.alSeleccionar) callbacks.current.alSeleccionar(ev);
  };
  const fallar = (texto) => {
    setEvidencia(null);
    if (callbacks.current.alError) callbacks.current.alError(texto);
  };

  const alElegirFoto = async (evento) => {
    const archivo = evento.target.files && evento.target.files[0];
    evento.target.value = '';
    if (!archivo) return;
    setProcesando(true);
    try {
      const r = await procesarFoto(archivo);
      if (!montado.current) return;
      if (!r.blob || r.blob.size === 0 || r.blob.size > LIMITE_ARCHIVO_BYTES) {
        fallar('La foto procesada no tiene un tamaño válido para subirla. Intenta tomarla de nuevo.');
        return;
      }
      fijar({
        origen: 'foto', blob: r.blob, nombre: callbacks.current.nombreFoto(), tipo: 'Imagen JPEG',
        tamano: r.blob.size, tamanoOriginal: archivo.size, dimensiones: r.destino,
        preview: URL.createObjectURL(r.blob)
      });
    } catch {
      if (montado.current) fallar('No se pudo procesar la foto. Intenta de nuevo.');
    } finally {
      if (montado.current) setProcesando(false);
    }
  };

  const alElegirArchivo = (evento) => {
    const archivo = evento.target.files && evento.target.files[0];
    evento.target.value = '';
    if (!archivo) return;
    const problema = validarArchivoManual(archivo);
    if (problema) { fallar(problema); return; }
    const esImagen = ['jpg', 'jpeg', 'png'].includes(extension(archivo.name));
    fijar({
      origen: 'archivo', blob: archivo, nombre: archivo.name, tipo: tipoLegible(archivo.name),
      tamano: archivo.size, preview: esImagen ? URL.createObjectURL(archivo) : null
    });
  };

  const entradas = (
    <>
      <input ref={inputFoto} type="file" accept="image/*" capture="environment" hidden onChange={alElegirFoto} data-carga="foto" />
      <input ref={inputArchivo} type="file" accept={ACCEPT_ARCHIVO} hidden onChange={alElegirArchivo} data-carga="archivo" />
    </>
  );

  return {
    evidencia,
    procesando,
    descartar: () => setEvidencia(null),
    abrirCamara: () => inputFoto.current && inputFoto.current.click(),
    abrirArchivo: () => inputArchivo.current && inputArchivo.current.click(),
    entradas
  };
}

// Vista previa + datos básicos de la evidencia (foto o archivo).
export function InfoEvidencia({ evidencia }) {
  return (
    <div className="m-carga-archivo">
      {evidencia.preview
        ? <img className="m-carga-preview" src={evidencia.preview} alt={`Vista previa de ${evidencia.nombre}`} />
        : <span className="m-carga-icono" aria-hidden="true"><i className="ti ti-file-type-pdf"></i></span>}
      <dl className="m-carga-info">
        <div><dt className="m-sr-only">Nombre</dt><dd className="m-carga-nombre">{evidencia.nombre}</dd></div>
        <div><dt className="m-sr-only">Tipo y tamaño</dt><dd>{evidencia.tipo} · {formatearTamano(evidencia.tamano)}</dd></div>
        {evidencia.origen === 'foto' && evidencia.dimensiones && (
          <div><dt className="m-sr-only">Resolución</dt><dd>{evidencia.dimensiones.ancho} × {evidencia.dimensiones.alto} px · original {formatearTamano(evidencia.tamanoOriginal)}</dd></div>
        )}
      </dl>
    </div>
  );
}

// Guardia de salida para MobileShell (Atrás Android, flecha ←, navegación
// inferior): mientras `activa`, salir pide confirmación en la UI.
export function useGuardiaSalida(guardiaSalida, activa) {
  const [salidaPendiente, setSalidaPendiente] = useState(null);
  useEffect(() => {
    if (!guardiaSalida) return undefined;
    const propia = activa ? { confirmar: continuar => setSalidaPendiente(() => continuar) } : null;
    guardiaSalida.current = propia;
    return () => { if (guardiaSalida.current === propia) guardiaSalida.current = null; };
  }, [activa, guardiaSalida]);
  return [salidaPendiente, setSalidaPendiente];
}

export function DialogoSalida({ subiendo, textoPendiente, onSeguir, onSalir }) {
  return (
    <div className="m-dialogo-fondo">
      <div className="m-dialogo" role="alertdialog" aria-modal="true" aria-labelledby="m-dialogo-titulo" aria-describedby="m-dialogo-texto">
        <h2 id="m-dialogo-titulo">{subiendo ? 'Carga en curso' : 'Documento sin subir'}</h2>
        <p id="m-dialogo-texto">
          {subiendo
            ? 'Si sales ahora no verás la confirmación de la carga. Después revisa antes de volver a subirlo.'
            : textoPendiente}
        </p>
        <div className="m-carga-botones">
          <button type="button" className="m-btn m-btn-secundario" onClick={onSeguir}>Seguir aquí</button>
          <button type="button" className="m-btn m-btn-peligro" onClick={onSalir}>{subiendo ? 'Salir' : 'Descartar y salir'}</button>
        </div>
      </div>
    </div>
  );
}
